import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { parseQRValue } from './QRCodeDisplay';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Camera, CameraOff, Scan, Check, X, Loader2 } from 'lucide-react';

interface QRScannerProps {
  onScan: (result: { type: 'shipment' | 'invoice'; id: string }) => void;
  onError?: (error: string) => void;
  autoClose?: boolean;
  className?: string;
}

/**
 * QR Scanner Component
 * 
 * Self-contained camera scanner for QR codes.
 * Uses html5-qrcode library for real-time QR code detection.
 * 
 * Features:
 * - Real-time QR code scanning from camera
 * - Auto-focus on QR codes
 * - Visual feedback for scanning
 * - Error handling
 * - Stop/start camera controls
 * 
 * Easy to remove: Delete entire /components/qr/ directory
 */
export function QRScanner({
  onScan,
  onError,
  autoClose = true,
  className = ''
}: QRScannerProps) {
  const [isScanning, setIsScanning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ type: 'shipment' | 'invoice'; id: string } | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const qrCodeRegionId = useRef(`qr-reader-${Math.random().toString(36).substr(2, 9)}`);

  // Handle QR code detection
  const handleQRDetected = useCallback((decodedText: string) => {
    const parsed = parseQRValue(decodedText);
    
    if (parsed) {
      setLastResult(parsed);
      onScan(parsed);
      
      if (autoClose) {
        setTimeout(() => stopCamera(), 1000);
      }
    }
  }, [onScan, autoClose]);

  // Start camera and QR scanner
  const startCamera = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Stop existing scanner first if running
      if (scannerRef.current) {
        const state = await scannerRef.current.getState();
        if (state === 2) { // 2 = SCANNING
          await scannerRef.current.stop();
        }
      }

      // Create scanner instance if it doesn't exist
      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(qrCodeRegionId.current);
      }

      // Start scanning
      await scannerRef.current.start(
        { facingMode: "environment" }, // Use back camera on mobile
        {
          fps: 10, // Scan 10 times per second
          qrbox: { width: 250, height: 250 }, // Scanning box size
          aspectRatio: 1.0,
        },
        handleQRDetected,
        () => {
          // Error callback - silent, just means no QR code detected
        }
      );

      setIsScanning(true);
    } catch (err) {
      // Only show error if it's a real camera permission error
      const errorMessage = err instanceof Error ? err.message : 'Camera access denied';
      
      // Ignore "scan is ongoing" errors - they're handled internally
      if (!errorMessage.includes('scan is ongoing') && !errorMessage.includes('Cannot clear')) {
        setError(errorMessage);
        onError?.(errorMessage);
        console.error('QR Scanner Error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [handleQRDetected, onError]);

  // Stop camera and QR scanner
  const stopCamera = useCallback(async () => {
    if (scannerRef.current && isScanning) {
      try {
        await scannerRef.current.stop();
        setIsScanning(false);
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
    }
  }, [isScanning]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try {
          const state = scannerRef.current.getState();
          if (state === 2) { // Only stop if scanning
            scannerRef.current.stop().catch(() => {
              // Ignore errors on cleanup
            });
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  // Start camera when component mounts
  useEffect(() => {
    startCamera();
  }, [startCamera]);

  return (
    <Card className={`w-full max-w-md mx-auto ${className}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Scan className="h-5 w-5" />
          QR Code Scanner
        </CardTitle>
        <CardDescription>
          Position QR code within the camera view
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Camera View */}
        <div className="relative">
          <div 
            id={qrCodeRegionId.current}
            className="w-full rounded-lg overflow-hidden"
            style={{ minHeight: '300px' }}
          />

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <Loader2 className="h-8 w-8 text-white animate-spin" />
            </div>
          )}

          {/* Error overlay */}
          {error && (
            <div className="absolute inset-0 bg-red-500 bg-opacity-10 flex items-center justify-center rounded-lg">
              <div className="text-center text-red-600 p-4">
                <X className="h-8 w-8 mx-auto mb-2" />
                <p className="text-sm font-medium">Camera Error</p>
                <p className="text-xs mt-1">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex gap-2">
          {isScanning ? (
            <Button
              onClick={stopCamera}
              variant="outline"
              className="flex-1"
            >
              <CameraOff className="h-4 w-4 mr-2" />
              Stop Camera
            </Button>
          ) : (
            <Button
              onClick={startCamera}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              <Camera className="h-4 w-4 mr-2" />
              Start Camera
            </Button>
          )}
        </div>

        {/* Last Result */}
        {lastResult && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-green-700">
              <Check className="h-4 w-4" />
              <span className="text-sm font-medium">Scan Successful!</span>
            </div>
            <div className="mt-1 text-xs text-green-600">
              {lastResult.type === 'shipment' ? 'Shipment' : 'Invoice'}: {lastResult.id}
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="text-xs text-gray-500 space-y-1 bg-gray-50 p-3 rounded-lg">
          <p className="font-medium text-gray-700">Scanning Tips:</p>
          <p>• Ensure good lighting for best results</p>
          <p>• Hold device steady and align QR code in the frame</p>
          <p>• Keep the QR code flat and clearly visible</p>
          <p>• Move closer or farther to adjust focus</p>
        </div>
      </CardContent>
    </Card>
  );
}
